import { useEffect, useState, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { LockSimple, LockOpen, DownloadSimple, Printer, WhatsappLogo, Phone, ArrowsClockwise, FunnelSimple, Receipt } from '@phosphor-icons/react';
import { toast } from 'sonner';

// --- Constants & helpers ----------------------------------------------------

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);
const fmt2 = (n) => new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const TXN_TYPES = [
  { value: 'new_invoice',          label: 'New Invoice',         color: 'bg-[#DBEAFE] text-[#1D4ED8] border-[#BFDBFE]' },
  { value: 'full_payment',         label: 'Full Payment',        color: 'bg-[#DCFCE7] text-[#15803D] border-[#86EFAC]' },
  { value: 'partial_payment',      label: 'Partial Payment',     color: 'bg-[#DBEAFE] text-[#1D4ED8] border-[#BFDBFE]' },
  { value: 'advance_received',     label: 'Advance Received',    color: 'bg-[#EDE9FE] text-[#6D28D9] border-[#DDD6FE]' },
  { value: 'old_pending_received', label: 'Old Pending Received', color: 'bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]' },
  { value: 'expense',              label: 'Expense',             color: 'bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]' },
  { value: 'refund',               label: 'Refund',              color: 'bg-[#EDE9FE] text-[#6D28D9] border-[#DDD6FE]' },
  { value: 'advance_used',         label: 'Advance Used',        color: 'bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]' },
  { value: 'transfer_in',          label: 'Transfer In',         color: 'bg-[#E0F2FE] text-[#0369A1] border-[#BAE6FD]' },
  { value: 'transfer_out',         label: 'Transfer Out',        color: 'bg-[#E0F2FE] text-[#0369A1] border-[#BAE6FD]' },
];

const STATUS_BADGE = {
  paid:      'bg-[#DCFCE7] text-[#15803D] border-[#86EFAC]',
  partial:   'bg-[#DBEAFE] text-[#1D4ED8] border-[#BFDBFE]',
  pending:   'bg-[#FFEDD5] text-[#9A3412] border-[#FED7AA]',
  overdue:   'bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]',
  cancelled: 'bg-[#F4F4F5] text-[#52525B] border-[#E4E4E7]',
  refunded:  'bg-[#EDE9FE] text-[#6D28D9] border-[#DDD6FE]',
  expense:   'bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]',
};

const csvDownload = (filename, columns, rows) => {
  const esc = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [columns.map(c => esc(c.label || c.key)).join(','),
                 ...rows.map(r => columns.map(c => esc(c.format ? c.format(r[c.key], r) : r[c.key])).join(','))];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

// --- Page ------------------------------------------------------------------

export default function DailyTransactionPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  // Phase B — optional date range. When `rangeMode` is true the report
  // aggregates the [from, to] window instead of a single day.
  const [rangeMode, setRangeMode] = useState(false);
  const [fromDate, setFromDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [toDate, setToDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [staffList, setStaffList] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [filters, setFilters] = useState({
    staff: '', customer: '', invoice_no: '', service: '',
    payment_mode: '', txn_type: '', status: '',
  });
  const [data, setData] = useState(null);
  const [compareData, setCompareData] = useState(null);
  const [compareOn, setCompareOn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showClosing, setShowClosing] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState([]);

  useEffect(() => {
    api.get('/branches').then(r => {
      setBranches(r.data || []);
      const main = (r.data || []).find(b => b.is_main) || (r.data || [])[0];
      if (main) setBranchId(main.id);
    }).catch(() => {});
    api.get('/users').then(r => setStaffList(r.data || [])).catch(() => {});
    api.get('/finance/accounts').then(r => setAccounts((r.data || []).filter(a => !a.system))).catch(() => {});
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (rangeMode) {
        params.set('from', fromDate);
        params.set('to', toDate);
      } else {
        params.set('date', date);
      }
      if (branchId) params.set('branch_id', branchId);
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const { data: blob } = await api.get(`/daily-report?${params.toString()}`);
      setData(blob);
      setPendingApprovals(blob.pending_approvals || []);
      if (compareOn) {
        try {
          const { data: cmp } = await api.get(`/daily-report/compare?${params.toString()}`);
          setCompareData(cmp);
        } catch (_) { setCompareData(null); }
      } else {
        setCompareData(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [date, rangeMode, fromDate, toDate, branchId, filters]);

  useEffect(() => { refresh(); }, [refresh]);

  if (!isAdmin) {
    return <div className="p-8 text-center"><p className="text-sm text-[#EF4444]">Admin access required</p></div>;
  }

  const s = data?.summary_cards || {};
  const isLocked = !!data?.is_locked;
  const headerLabel = rangeMode
    ? `${new Date(fromDate + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} → ${new Date(toDate + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`
    : new Date(date + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });

  const buildExportUrl = (suffix) => {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    const p = new URLSearchParams();
    if (rangeMode) { p.set('from', fromDate); p.set('to', toDate); }
    else p.set('date', date);
    if (branchId) p.set('branch_id', branchId);
    Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
    return `${base}/api/daily-report/${suffix}?${p.toString()}`;
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="daily-report-page">
      {/* 1. HEADER ─────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#52525B]">Daily Transaction Report</p>
          <p className="text-2xl font-bold tracking-tight mt-1" style={{ fontFamily: 'var(--font-heading)' }}>
            {headerLabel}
            {isLocked && <Badge className="ml-3 rounded-md text-[10px] bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]" variant="outline"><LockSimple size={10} className="mr-1 inline" />DAY CLOSED</Badge>}
          </p>
          <p className="text-xs text-[#A1A1AA] mt-1">Branch: {(branches.find(b => b.id === branchId) || {}).name || '—'}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" className="rounded-lg" onClick={refresh} data-testid="refresh-btn">
            <ArrowsClockwise size={14} className="mr-1.5" /> Refresh
          </Button>
          <Button asChild variant="outline" size="sm" className="rounded-lg">
            <a href={buildExportUrl('print')} target="_blank" rel="noreferrer" data-testid="pdf-btn"><Printer size={14} className="mr-1.5" /> Print / PDF</a>
          </Button>
          <Button asChild variant="outline" size="sm" className="rounded-lg">
            <a href={buildExportUrl('excel')} data-testid="excel-btn"><DownloadSimple size={14} className="mr-1.5" /> Excel</a>
          </Button>
          <Button variant={compareOn ? 'default' : 'outline'} size="sm"
            className={`rounded-lg ${compareOn ? 'bg-[#7C3AED] text-white hover:bg-[#6D28D9] hover:text-white' : ''}`}
            onClick={() => setCompareOn(v => !v)} data-testid="compare-btn">
            <FunnelSimple size={14} className="mr-1.5" /> Compare {compareOn ? '(on)' : ''}
          </Button>
          <Button onClick={() => setShowClosing(true)}
            className={`rounded-xl text-white ${isLocked ? 'bg-[#EF4444] hover:bg-[#DC2626] hover:text-white' : 'bg-[#10B981] hover:bg-[#059669] hover:text-white'}`}
            data-testid="day-closing-btn">
            {isLocked ? <><LockOpen size={14} className="mr-1.5" /> Unlock Day</> : <><LockSimple size={14} className="mr-1.5" /> Close Day</>}
          </Button>
        </div>
      </div>

      {/* 2. FILTERS ────────────────────────────────────────────────── */}
      <Card className="rounded-xl border-[#E4E4E7] shadow-sm">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {rangeMode ? (
              <>
                <Field label="From"><Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="rounded-lg" data-testid="filter-from" /></Field>
                <Field label="To"><Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="rounded-lg" data-testid="filter-to" /></Field>
              </>
            ) : (
              <Field label="Date"><Input type="date" value={date} onChange={e => setDate(e.target.value)} className="rounded-lg" data-testid="filter-date" /></Field>
            )}
            <Field label={rangeMode ? "Range mode (on)" : "Single day"}>
              <Button variant="outline" size="sm" className="rounded-lg w-full justify-start" onClick={() => setRangeMode(v => !v)} data-testid="toggle-range-btn">
                {rangeMode ? '← Switch to single day' : 'Switch to date range →'}
              </Button>
            </Field>
            <Field label="Branch"><Select value={branchId} onValueChange={setBranchId}>
              <SelectTrigger className="rounded-lg" data-testid="filter-branch"><SelectValue placeholder="Branch…" /></SelectTrigger>
              <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
            </Select></Field>
            <Field label="Staff"><Select value={filters.staff || '__all__'} onValueChange={v => setFilters(f => ({ ...f, staff: v === '__all__' ? '' : v }))}>
              <SelectTrigger className="rounded-lg" data-testid="filter-staff"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Staff</SelectItem>
                {staffList.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select></Field>
            <Field label="Customer name / mobile"><Input value={filters.customer} onChange={e => setFilters(f => ({ ...f, customer: e.target.value }))} placeholder="Search…" className="rounded-lg" data-testid="filter-customer" /></Field>
            <Field label="Invoice number"><Input value={filters.invoice_no} onChange={e => setFilters(f => ({ ...f, invoice_no: e.target.value }))} placeholder="INV-…" className="rounded-lg" data-testid="filter-invoice" /></Field>
            <Field label="Service / category"><Input value={filters.service} onChange={e => setFilters(f => ({ ...f, service: e.target.value }))} placeholder="Aadhaar, BBPS…" className="rounded-lg" data-testid="filter-service" /></Field>
            <Field label="Payment mode"><Select value={filters.payment_mode || '__all__'} onValueChange={v => setFilters(f => ({ ...f, payment_mode: v === '__all__' ? '' : v }))}>
              <SelectTrigger className="rounded-lg" data-testid="filter-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Modes</SelectItem>
                {accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select></Field>
            <Field label="Transaction type"><Select value={filters.txn_type || '__all__'} onValueChange={v => setFilters(f => ({ ...f, txn_type: v === '__all__' ? '' : v }))}>
              <SelectTrigger className="rounded-lg" data-testid="filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Types</SelectItem>
                {TXN_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select></Field>
            <Field label="Status"><Select value={filters.status || '__all__'} onValueChange={v => setFilters(f => ({ ...f, status: v === '__all__' ? '' : v }))}>
              <SelectTrigger className="rounded-lg" data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select></Field>
            <div className="flex items-end">
              <Button variant="outline" size="sm" className="rounded-lg w-full"
                onClick={() => setFilters({ staff: '', customer: '', invoice_no: '', service: '', payment_mode: '', txn_type: '', status: '' })}
                data-testid="reset-filters-btn">
                <FunnelSimple size={14} className="mr-1.5" /> Reset
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 3. SUMMARY CARDS (12) ─────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          ['Opening Cash',    'opening_cash',    'text-[#52525B]', 'card-opening'],
          ['Total Invoice',   'total_invoice',   'text-[#3B82F6]', 'card-invoice'],
          ['Total Received',  'total_received',  'text-[#10B981]', 'card-received'],
          ['Cash',            'cash_received',   'text-[#10B981]', 'card-cash'],
          ['UPI',             'upi_received',    'text-[#7C3AED]', 'card-upi'],
          ['Bank',            'bank_received',   'text-[#0369A1]', 'card-bank'],
          ['Wallet',          'wallet_received', 'text-[#D97706]', 'card-wallet'],
          ['Total Expense',   'total_expense',   'text-[#EF4444]', 'card-expense'],
          ['Pending',         'pending_amount',  'text-[#F59E0B]', 'card-pending'],
          ['Net Cash In Hand','net_cash',        'text-[#15803D]', 'card-netcash'],
          ['Today\'s Profit', 'today_profit',    'text-[#10B981]', 'card-profit'],
          ['Closing Cash',    'closing_cash',    'text-[#0A0A0A]', 'card-closing'],
        ].map(([lbl, k, color, tid]) => (
          <SummaryCard key={k} label={lbl} value={s[k]} color={color} testid={tid}
            delta={compareData?.delta_pct?.[k]}
            previous={compareData?.previous?.summary_cards?.[k]} />
        ))}
      </div>

      {/* 4. PAYMENT MODE SUMMARY ──────────────────────────────────── */}
      <Section title="Payment Mode Summary" testid="mode-summary">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 p-5">
          {[
            ['Cash', s.cash_received, '#10B981'],
            ['UPI', s.upi_received, '#7C3AED'],
            ['Bank', s.bank_received, '#0369A1'],
            ['Wallet', s.wallet_received, '#D97706'],
            ['Other', s.other_received, '#52525B'],
          ].map(([label, val, color]) => (
            <div key={label} className="border rounded-xl p-3 text-center" style={{ borderColor: `${color}33`, backgroundColor: `${color}0D` }}>
              <p className="text-[10px] uppercase tracking-[0.12em] font-semibold" style={{ color }}>{label}</p>
              <p className="text-2xl font-bold font-mono mt-1" style={{ color }}>{fmt(val)}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* 5. MAIN TRANSACTION TABLE ────────────────────────────────── */}
      <Section title="Transactions" testid="txn-table"
        right={<ExportButtons rows={data?.transactions || []} csvName={`transactions-${date}.csv`} columns={[
          { key: 'time', label: 'Time', format: v => v ? new Date(v).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '' },
          { key: 'invoice_number', label: 'Invoice' },
          { key: 'customer_name', label: 'Customer' },
          { key: 'mobile', label: 'Mobile' },
          { key: 'service_name', label: 'Service' },
          { key: 'staff_name', label: 'Staff' },
          { key: 'transaction_type', label: 'Type' },
          { key: 'bill_amount', label: 'Bill' },
          { key: 'govt_fee', label: 'Govt Fee' },
          { key: 'service_charge', label: 'Svc Charge' },
          { key: 'received_amount', label: 'Received' },
          { key: 'pending_amount', label: 'Pending' },
          { key: 'payment_mode', label: 'Mode' },
          { key: 'status', label: 'Status' },
        ]} />}>
        <TxnTable rows={data?.transactions || []} loading={loading} />
      </Section>

      {/* 6. PENDING COLLECTION ────────────────────────────────────── */}
      <Section title="Pending Collection" testid="pending-section"
        right={<ExportButtons rows={data?.pending_collection || []} csvName={`pending-${date}.csv`} columns={[
          { key: 'invoice_number', label: 'Invoice' }, { key: 'customer_name', label: 'Customer' },
          { key: 'mobile', label: 'Mobile' }, { key: 'service_name', label: 'Service' },
          { key: 'total', label: 'Total' }, { key: 'received', label: 'Received' }, { key: 'balance', label: 'Balance' },
          { key: 'due_days', label: 'Due Days' }, { key: 'staff_name', label: 'Staff' }, { key: 'followup_note', label: 'Note' },
        ]} />}>
        <PendingTable rows={data?.pending_collection || []} loading={loading} />
      </Section>

      {/* 7. PENDING APPROVALS (admin-only expense workflow) ────────── */}
      {pendingApprovals.length > 0 && (
        <div data-testid="pending-approvals-section">
        <Section title={`Pending Approvals · ${pendingApprovals.filter(p => p.approval_status === 'pending').length} pending`} testid="approvals-section">
          <PendingApprovalsTable rows={pendingApprovals} onAction={refresh} />
        </Section>
        </div>
      )}

      {/* 8. SERVICE-WISE REPORT ───────────────────────────────────── */}
      <Section title="Service-wise Report" testid="service-section"
        right={<ExportButtons rows={data?.service_wise || []} csvName={`service-wise-${date}.csv`} columns={[
          { key: 'service_name', label: 'Service' }, { key: 'applications', label: 'Applications' },
          { key: 'bill_amount', label: 'Bill' }, { key: 'govt_fee', label: 'Govt Fee' },
          { key: 'service_charge', label: 'Svc Charge' }, { key: 'linked_expense', label: 'Linked Expense' },
          { key: 'profit', label: 'Profit' },
        ]} />}>
        <SimpleTable rows={data?.service_wise || []} loading={loading} columns={[
          { key: 'service_name', label: 'Service', cls: 'font-medium' },
          { key: 'applications', label: 'Applications', cls: 'text-right font-mono' },
          { key: 'bill_amount', label: 'Bill Amount', cls: 'text-right font-mono', fmt },
          { key: 'govt_fee', label: 'Govt Fee', cls: 'text-right font-mono', fmt },
          { key: 'service_charge', label: 'Svc Charge', cls: 'text-right font-mono text-[#10B981]', fmt },
          { key: 'linked_expense', label: 'Linked Exp', cls: 'text-right font-mono text-[#EF4444]', fmt },
          { key: 'profit', label: 'Profit', cls: 'text-right font-mono text-[#10B981] font-bold', fmt },
        ]} />
      </Section>

      {/* 8. STAFF COLLECTION ──────────────────────────────────────── */}
      <Section title="Staff Collection" testid="staff-section"
        right={<ExportButtons rows={data?.staff_wise || []} csvName={`staff-collection-${date}.csv`} columns={[
          { key: 'staff_name', label: 'Staff' }, { key: 'bills_created', label: 'Bills' },
          { key: 'cash_collected', label: 'Cash' }, { key: 'bank_collected', label: 'Bank' },
          { key: 'wallet_collected', label: 'Wallet' }, { key: 'upi_collected', label: 'UPI' },
          { key: 'pending_amount', label: 'Pending' }, { key: 'expense_entered', label: 'Expense' },
          { key: 'net_collection', label: 'Net' },
        ]} />}>
        <SimpleTable rows={data?.staff_wise || []} loading={loading} columns={[
          { key: 'staff_name', label: 'Staff', cls: 'font-medium' },
          { key: 'bills_created', label: 'Bills', cls: 'text-right font-mono' },
          { key: 'cash_collected', label: 'Cash', cls: 'text-right font-mono text-[#10B981]', fmt },
          { key: 'bank_collected', label: 'Bank', cls: 'text-right font-mono text-[#0369A1]', fmt },
          { key: 'wallet_collected', label: 'Wallet', cls: 'text-right font-mono text-[#D97706]', fmt },
          { key: 'upi_collected', label: 'UPI', cls: 'text-right font-mono text-[#7C3AED]', fmt },
          { key: 'pending_amount', label: 'Pending', cls: 'text-right font-mono text-[#F59E0B]', fmt },
          { key: 'expense_entered', label: 'Expense', cls: 'text-right font-mono text-[#EF4444]', fmt },
          { key: 'net_collection', label: 'Net', cls: 'text-right font-mono font-bold', fmt },
        ]} />
      </Section>

      {/* 9. EXPENSE SUMMARY ───────────────────────────────────────── */}
      <Section title="Expense Summary" testid="expense-section"
        right={<ExportButtons rows={data?.expenses || []} csvName={`expenses-${date}.csv`} columns={[
          { key: 'time', label: 'Time', format: v => v ? new Date(v).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '' },
          { key: 'category', label: 'Category' }, { key: 'description', label: 'Description' },
          { key: 'amount', label: 'Amount' }, { key: 'payment_mode', label: 'Mode' },
          { key: 'staff_name', label: 'Staff' }, { key: 'approved_by', label: 'Approved By' },
        ]} />}>
        <SimpleTable rows={data?.expenses || []} loading={loading} columns={[
          { key: 'time', label: 'Time', fmtCell: v => v ? new Date(v).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '' },
          { key: 'category', label: 'Category', cls: 'font-medium' },
          { key: 'description', label: 'Description', cls: 'text-[#52525B] truncate max-w-[300px]' },
          { key: 'amount', label: 'Amount', cls: 'text-right font-mono text-[#EF4444]', fmt },
          { key: 'payment_mode', label: 'Mode' },
          { key: 'staff_name', label: 'Staff' },
          { key: 'approved_by', label: 'Approved By', fmtCell: v => v || '—' },
        ]} />
      </Section>

      {/* 10. AUDIT LOG ─────────────────────────────────────────────── */}
      <Section title="Audit Log (Day Closing)" testid="audit-section">
        {(data?.audit || []).length === 0 ? (
          <p className="p-6 text-sm text-[#A1A1AA] text-center">No close/unlock events on this date.</p>
        ) : (
          <SimpleTable rows={data?.audit || []} loading={false} columns={[
            { key: 'created_at', label: 'Time', fmtCell: v => v ? new Date(v).toLocaleString('en-IN') : '' },
            { key: 'event_type', label: 'Event', fmtCell: v => v === 'day_closed' ? 'DAY CLOSED' : 'DAY UNLOCKED' },
            { key: 'user_name', label: 'By' },
            { key: 'ip', label: 'IP' },
          ]} />
        )}
      </Section>

      {/* 11. DAY CLOSING DIALOG (also shows existing closing summary) ─── */}
      <DayClosingDialog open={showClosing} onClose={() => setShowClosing(false)}
        date={date} branchId={branchId} existing={data?.closing}
        onSaved={refresh} />
    </div>
  );
}

// --- Small composables ------------------------------------------------------

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function SummaryCard({ label, value, color, testid, delta, previous }) {
  const hasDelta = delta !== undefined && delta !== null;
  const deltaPositive = hasDelta && delta > 0;
  const deltaNegative = hasDelta && delta < 0;
  return (
    <Card className="rounded-xl border-[#E4E4E7] shadow-sm" data-testid={testid}>
      <CardContent className="p-4">
        <p className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B] truncate">{label}</p>
        <p className={`text-xl font-bold font-mono mt-1 ${color}`}>{fmt(value)}</p>
        {hasDelta && (
          <div className="mt-1 flex items-center gap-1">
            <span className={`text-[10px] font-mono font-semibold ${deltaPositive ? 'text-[#15803D]' : deltaNegative ? 'text-[#B91C1C]' : 'text-[#A1A1AA]'}`}>
              {deltaPositive ? '▲' : deltaNegative ? '▼' : '–'} {Math.abs(delta).toFixed(1)}%
            </span>
            <span className="text-[9px] text-[#A1A1AA]">vs prev {fmt(previous)}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Section({ title, children, right, testid }) {
  return (
    <Card className="rounded-xl border-[#E4E4E7] shadow-sm" data-testid={testid}>
      <CardHeader className="p-5 pb-3 flex-row items-center justify-between">
        <CardTitle className="text-base font-semibold">{title}</CardTitle>
        {right}
      </CardHeader>
      <CardContent className="p-0">{children}</CardContent>
    </Card>
  );
}

function ExportButtons({ rows, columns, csvName }) {
  return (
    <Button variant="ghost" size="sm" className="text-xs text-[#3B82F6]" disabled={!rows.length}
      onClick={() => csvDownload(csvName, columns, rows)}>
      <DownloadSimple size={14} className="mr-1" /> CSV
    </Button>
  );
}

function TxnTable({ rows, loading }) {
  if (loading) return <p className="p-6 text-sm text-[#52525B] text-center">Loading…</p>;
  if (!rows.length) return <p className="p-8 text-sm text-[#A1A1AA] text-center">No transactions on this date.</p>;
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="border-[#E4E4E7]">
            {['Time', 'Invoice', 'Customer', 'Mobile', 'Service', 'Staff', 'Type', 'Bill', 'Govt Fee', 'Svc', 'Received', 'Pending', 'Mode', 'Status'].map(h => (
              <TableHead key={h} className="text-[10px] uppercase tracking-[0.1em] font-semibold whitespace-nowrap">{h}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r, i) => {
            const tt = TXN_TYPES.find(x => x.value === r.transaction_type);
            return (
              <TableRow key={i} className="border-[#E4E4E7]" data-testid={`txn-row-${i}`}>
                <TableCell className="text-xs font-mono whitespace-nowrap">{r.time ? new Date(r.time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—'}</TableCell>
                <TableCell className="text-xs font-mono text-[#3B82F6]">{r.invoice_number || '—'}</TableCell>
                <TableCell className="text-xs">{r.customer_name || '—'}</TableCell>
                <TableCell className="text-xs text-[#52525B]">{r.mobile || '—'}</TableCell>
                <TableCell className="text-xs max-w-[200px] truncate" title={r.service_name}>{r.service_name || '—'}</TableCell>
                <TableCell className="text-xs">{r.staff_name || '—'}</TableCell>
                <TableCell><Badge className={`rounded-md text-[10px] whitespace-nowrap ${tt?.color || 'bg-[#F4F4F5]'}`} variant="outline">{tt?.label || r.transaction_type}</Badge></TableCell>
                <TableCell className="text-xs text-right font-mono">{r.bill_amount ? fmt(r.bill_amount) : '—'}</TableCell>
                <TableCell className="text-xs text-right font-mono text-[#92400E]">{r.govt_fee ? fmt(r.govt_fee) : '—'}</TableCell>
                <TableCell className="text-xs text-right font-mono text-[#10B981]">{r.service_charge ? fmt(r.service_charge) : '—'}</TableCell>
                <TableCell className="text-xs text-right font-mono text-[#15803D]">{r.received_amount ? fmt(r.received_amount) : '—'}</TableCell>
                <TableCell className="text-xs text-right font-mono text-[#F59E0B]">{r.pending_amount ? fmt(r.pending_amount) : '—'}</TableCell>
                <TableCell className="text-xs whitespace-nowrap">{r.payment_mode || '—'}</TableCell>
                <TableCell><Badge className={`rounded-md text-[10px] ${STATUS_BADGE[r.status] || 'bg-[#F4F4F5]'}`} variant="outline">{r.status || '—'}</Badge></TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function PendingTable({ rows, loading }) {
  if (loading) return <p className="p-6 text-sm text-[#52525B] text-center">Loading…</p>;
  if (!rows.length) return <p className="p-8 text-sm text-[#A1A1AA] text-center">No outstanding invoices.</p>;
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="border-[#E4E4E7]">
            {['Invoice', 'Customer', 'Mobile', 'Service', 'Total', 'Received', 'Balance', 'Days', 'Staff', 'Note', 'Actions'].map(h => (
              <TableHead key={h} className="text-[10px] uppercase tracking-[0.1em] font-semibold whitespace-nowrap">{h}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(r => {
            const phone = (r.mobile || '').replace(/\D/g, '');
            const msg = encodeURIComponent(`Dear ${r.customer_name}, please clear pending dues of ₹${fmt2(r.balance)} against invoice ${r.invoice_number}. Thank you.`);
            const overdueColor = r.due_days > 30 ? 'text-[#B91C1C]' : r.due_days > 7 ? 'text-[#D97706]' : 'text-[#52525B]';
            return (
              <TableRow key={r.invoice_id} className="border-[#E4E4E7]" data-testid={`pending-row-${r.invoice_id}`}>
                <TableCell className="text-xs font-mono text-[#3B82F6]">{r.invoice_number}</TableCell>
                <TableCell className="text-xs">{r.customer_name}</TableCell>
                <TableCell className="text-xs text-[#52525B]">{r.mobile || '—'}</TableCell>
                <TableCell className="text-xs max-w-[200px] truncate" title={r.service_name}>{r.service_name}</TableCell>
                <TableCell className="text-xs text-right font-mono">{fmt(r.total)}</TableCell>
                <TableCell className="text-xs text-right font-mono text-[#10B981]">{fmt(r.received)}</TableCell>
                <TableCell className="text-xs text-right font-mono text-[#F59E0B] font-bold">{fmt(r.balance)}</TableCell>
                <TableCell className={`text-xs text-right font-mono ${overdueColor}`}>{r.due_days}d</TableCell>
                <TableCell className="text-xs">{r.staff_name || '—'}</TableCell>
                <TableCell className="text-xs text-[#52525B] max-w-[200px] truncate" title={r.followup_note}>{r.followup_note || '—'}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    {phone && (
                      <Button asChild variant="ghost" size="sm" className="h-7 px-2 text-[#25D366]" title="WhatsApp">
                        <a href={`https://wa.me/${phone}?text=${msg}`} target="_blank" rel="noreferrer" data-testid={`wa-${r.invoice_id}`}><WhatsappLogo size={14} weight="fill" /></a>
                      </Button>
                    )}
                    {phone && (
                      <Button asChild variant="ghost" size="sm" className="h-7 px-2 text-[#3B82F6]" title="Call">
                        <a href={`tel:${phone}`} data-testid={`call-${r.invoice_id}`}><Phone size={14} weight="fill" /></a>
                      </Button>
                    )}
                    <Button asChild variant="ghost" size="sm" className="h-7 px-2 text-[#52525B]" title="View invoice">
                      <a href={`/invoices/edit/${r.invoice_id}`} data-testid={`view-${r.invoice_id}`}><Receipt size={14} /></a>
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function PendingApprovalsTable({ rows, onAction }) {
  const [reject, setReject] = useState(null);    // { row, reason }
  const approve = async (row) => {
    try {
      await api.post(`/expense-approvals/${row.id}/approve`);
      toast.success(`Approved · ${row.voucher_number || row.id}`);
      onAction?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Approve failed'); }
  };
  const doReject = async () => {
    if (!reject) return;
    try {
      await api.post(`/expense-approvals/${reject.row.id}/reject`, { reason: reject.reason });
      toast.success(`Rejected · ${reject.row.voucher_number || reject.row.id}`);
      setReject(null);
      onAction?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Reject failed'); }
  };
  return (
    <>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-[#E4E4E7]">
              {['Time', 'Voucher', 'Category', 'Description', 'Amount', 'Mode', 'Staff', 'Status', 'Actions'].map(h => (
                <TableHead key={h} className="text-[10px] uppercase tracking-[0.1em] font-semibold whitespace-nowrap">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.id} className="border-[#E4E4E7]" data-testid={`approval-row-${r.id}`}>
                <TableCell className="text-xs font-mono">{r.time ? new Date(r.time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—'}</TableCell>
                <TableCell className="text-xs font-mono text-[#3B82F6]">{r.voucher_number || '—'}</TableCell>
                <TableCell className="text-xs font-medium">{r.category}</TableCell>
                <TableCell className="text-xs text-[#52525B] max-w-[260px] truncate" title={r.description}>{r.description || '—'}</TableCell>
                <TableCell className="text-xs text-right font-mono font-bold text-[#EF4444]">{fmt(r.amount)}</TableCell>
                <TableCell className="text-xs">{r.payment_mode || '—'}</TableCell>
                <TableCell className="text-xs">{r.staff_name || '—'}</TableCell>
                <TableCell>
                  <Badge className={`rounded-md text-[10px] ${r.approval_status === 'pending' ? 'bg-[#FFEDD5] text-[#9A3412] border-[#FED7AA]' : 'bg-[#F4F4F5] text-[#52525B] border-[#E4E4E7]'}`} variant="outline">
                    {r.approval_status?.toUpperCase()}
                  </Badge>
                </TableCell>
                <TableCell>
                  {r.approval_status === 'pending' && (
                    <div className="flex gap-1">
                      <Button size="sm" className="h-7 px-3 bg-[#10B981] text-white hover:bg-[#059669] hover:text-white text-xs" onClick={() => approve(r)} data-testid={`approve-${r.id}`}>Approve</Button>
                      <Button size="sm" variant="outline" className="h-7 px-3 border-[#EF4444] text-[#EF4444] hover:bg-[#FEE2E2] text-xs" onClick={() => setReject({ row: r, reason: '' })} data-testid={`reject-${r.id}`}>Reject</Button>
                    </div>
                  )}
                  {r.approval_status === 'rejected' && r.rejected_reason && (
                    <span className="text-[10px] text-[#B91C1C]" title={r.rejected_reason}>{r.rejected_reason.slice(0, 40)}</span>
                  )}
                  {r.approval_status === 'rejected' && (
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-[#10B981]" onClick={() => approve(r)} data-testid={`approve-${r.id}`}>Re-approve</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Dialog open={!!reject} onOpenChange={(v) => { if (!v) setReject(null); }}>
        <DialogContent className="rounded-xl border-[#E4E4E7] max-w-md" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject expense {reject?.row?.voucher_number}</DialogTitle>
            <DialogDescription>{fmt(reject?.row?.amount)} · {reject?.row?.category}</DialogDescription>
          </DialogHeader>
          <Textarea rows={3} placeholder="Reason (optional)" value={reject?.reason || ''}
            onChange={e => setReject(r => ({ ...r, reason: e.target.value }))} className="rounded-lg" data-testid="reject-reason" />
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setReject(null)}>Cancel</Button>
            <Button className="bg-[#EF4444] text-white hover:bg-[#DC2626] hover:text-white rounded-xl" onClick={doReject} data-testid="confirm-reject-btn">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function SimpleTable({ rows, loading, columns }) {
  if (loading) return <p className="p-6 text-sm text-[#52525B] text-center">Loading…</p>;
  if (!rows.length) return <p className="p-8 text-sm text-[#A1A1AA] text-center">No data.</p>;
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="border-[#E4E4E7]">
            {columns.map(c => <TableHead key={c.key} className={`text-[10px] uppercase tracking-[0.1em] font-semibold whitespace-nowrap ${c.cls?.includes('text-right') ? 'text-right' : ''}`}>{c.label}</TableHead>)}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i} className="border-[#E4E4E7]">
              {columns.map(c => (
                <TableCell key={c.key} className={`text-xs whitespace-nowrap ${c.cls || ''}`}>
                  {c.fmtCell ? c.fmtCell(r[c.key], r) : (c.fmt ? c.fmt(r[c.key]) : (r[c.key] ?? '—'))}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// --- Day Closing Dialog ----------------------------------------------------

function DayClosingDialog({ open, onClose, date, branchId, existing, onSaved }) {
  const isLocked = existing?.status === 'closed';
  const [form, setForm] = useState({
    opening_cash: 0, cash_received: 0, cash_expenses: 0, cash_to_bank: 0, actual_closing: 0, note: '',
  });
  useEffect(() => {
    if (open && existing) setForm({
      opening_cash: existing.opening_cash || 0,
      cash_received: existing.cash_received || 0,
      cash_expenses: existing.cash_expenses || 0,
      cash_to_bank: existing.cash_to_bank || 0,
      actual_closing: existing.actual_closing || 0,
      note: existing.note || '',
    });
    else if (open) setForm({ opening_cash: 0, cash_received: 0, cash_expenses: 0, cash_to_bank: 0, actual_closing: 0, note: '' });
  }, [open, existing]);

  const expected = useMemo(() => round2(
    Number(form.opening_cash) + Number(form.cash_received) - Number(form.cash_expenses) - Number(form.cash_to_bank)
  ), [form]);
  const diff = useMemo(() => round2(Number(form.actual_closing) - expected), [form, expected]);

  const save = async () => {
    try {
      await api.post('/day-closing', {
        date, branch_id: branchId,
        opening_cash: Number(form.opening_cash),
        cash_received: Number(form.cash_received),
        cash_expenses: Number(form.cash_expenses),
        cash_to_bank: Number(form.cash_to_bank),
        actual_closing: Number(form.actual_closing),
        note: form.note,
      });
      toast.success('Day closed');
      onClose();
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to close day');
    }
  };

  const unlock = async () => {
    if (!existing?.id) return;
    try {
      await api.post(`/day-closing/unlock/${existing.id}`);
      toast.success('Day unlocked — staff can edit again');
      onClose();
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unlock day');
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="rounded-xl border-[#E4E4E7] max-w-lg" data-testid="day-closing-dialog">
        <DialogHeader>
          <DialogTitle>{isLocked ? 'Day Closed' : 'Close Day'} · {date}</DialogTitle>
          <DialogDescription>
            {isLocked
              ? `Closed by ${existing.closed_by_name || '—'} on ${new Date(existing.closed_at).toLocaleString('en-IN')}. Staff cannot edit transactions on this date until you unlock it.`
              : 'Tally the day. Once closed, staff cannot modify any transaction dated on or before this date.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <NumField label="Opening Cash" value={form.opening_cash} onChange={v => setForm(f => ({ ...f, opening_cash: v }))} disabled={isLocked} testid="closing-opening" />
            <NumField label="Cash Received" value={form.cash_received} onChange={v => setForm(f => ({ ...f, cash_received: v }))} disabled={isLocked} testid="closing-received" />
            <NumField label="Cash Expenses" value={form.cash_expenses} onChange={v => setForm(f => ({ ...f, cash_expenses: v }))} disabled={isLocked} testid="closing-expenses" />
            <NumField label="Cash Transferred to Bank" value={form.cash_to_bank} onChange={v => setForm(f => ({ ...f, cash_to_bank: v }))} disabled={isLocked} testid="closing-tobank" />
          </div>
          <div className="bg-[#F4F4F5] border border-[#E4E4E7] rounded-lg p-3 flex justify-between items-center">
            <span className="text-xs uppercase tracking-[0.12em] font-semibold text-[#52525B]">Expected Closing Cash</span>
            <span className="text-lg font-bold font-mono">{fmt(expected)}</span>
          </div>
          <NumField label="Actual Closing Cash (counted)" value={form.actual_closing} onChange={v => setForm(f => ({ ...f, actual_closing: v }))} disabled={isLocked} testid="closing-actual" />
          <div className={`rounded-lg p-3 border flex justify-between items-center ${diff === 0 ? 'bg-[#DCFCE7] border-[#86EFAC]' : diff > 0 ? 'bg-[#DBEAFE] border-[#BFDBFE]' : 'bg-[#FEE2E2] border-[#FCA5A5]'}`}>
            <span className="text-xs uppercase tracking-[0.12em] font-semibold">{diff === 0 ? 'Balanced' : diff > 0 ? 'Surplus' : 'Shortage'}</span>
            <span className="text-lg font-bold font-mono">{fmt(Math.abs(diff))}</span>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Closing Note</Label>
            <Textarea rows={2} value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} disabled={isLocked} className="rounded-lg mt-1" data-testid="closing-note" />
          </div>
        </div>
        <DialogFooter>
          {isLocked
            ? <Button onClick={unlock} className="bg-[#EF4444] text-white hover:bg-[#DC2626] hover:text-white rounded-xl" data-testid="unlock-day-btn"><LockOpen size={14} className="mr-1.5" /> Unlock Day</Button>
            : <Button onClick={save} className="bg-[#10B981] text-white hover:bg-[#059669] hover:text-white rounded-xl" data-testid="close-day-btn"><LockSimple size={14} className="mr-1.5" /> Close Day</Button>
          }
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NumField({ label, value, onChange, disabled, testid }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">{label}</Label>
      <Input type="number" min={0} value={value} disabled={disabled} onChange={e => onChange(e.target.value)} className="rounded-lg mt-1 font-mono" data-testid={testid} />
    </div>
  );
}

function round2(n) { return Math.round((Number(n) + Number.EPSILON) * 100) / 100; }
